import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  ClipboardCheck,
  ScrollText,
  FileSignature,
  Building2,
  Compass,
  ShieldX,
  BookOpen,
  AlertTriangle,
  MapPin,
  ArrowUpRight,
  Camera,
  MessageCircle,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat, { whatsappUrl } from "@/components/site/WhatsAppFloat";
import StickyCTA from "@/components/site/StickyCTA";
import { useReveal } from "@/lib/reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format&fit=crop";

const cases = [
  { icon: Compass, text: "Vous souhaitez construire mais ne savez pas par où commencer." },
  { icon: ShieldX, text: "Vous avez peur que votre permis soit refusé." },
  { icon: BookOpen, text: "Les démarches vous semblent complexes." },
  { icon: AlertTriangle, text: "Vous voulez éviter les erreurs coûteuses." },
];

const services = [
  {
    icon: ClipboardCheck,
    title: "Analyse de votre projet",
    desc: "Étude de faisabilité, contraintes du terrain, identification des points de vigilance dès le départ.",
  },
  {
    icon: ScrollText,
    title: "Vérification des règles d'urbanisme",
    desc: "PLU communal, servitudes, contraintes paysagères : tout est passé au peigne fin avant la conception.",
  },
  {
    icon: FileSignature,
    title: "Constitution du dossier complet",
    desc: "Plans, notices, CERFA, insertion paysagère, modélisation 3D — toutes les pièces obligatoires.",
  },
  {
    icon: Building2,
    title: "Préparation au dépôt en mairie",
    desc: "Vérification finale, dépôt et suivi d'instruction jusqu'à l'obtention du permis.",
  },
];

const cities = [
  "Saint-Denis", "Saint-Pierre", "Saint-Paul", "Le Tampon", "Saint-André",
  "Saint-Benoît", "Saint-Leu", "L'Étang-Salé", "La Possession", "Sainte-Marie",
  "Sainte-Suzanne", "Saint-Joseph",
];

const reasons = [
  "Un seul interlocuteur",
  "Expertise locale",
  "Accompagnement complet",
  "Projet sécurisé",
  "+100 projets réalisés",
];

export default function PermisConstruireLaReunion() {
  useReveal();

  useEffect(() => {
    document.title = "Permis de construire à La Réunion (974) | Dossier complet & accompagnement";
    setMeta(
      "description",
      "Obtenez votre permis de construire à La Réunion avec Solmis Habitat. Dossier complet, étude de sol, accompagnement de A à Z. Devis gratuit."
    );
    setMeta(
      "keywords",
      "permis de construire La Réunion, permis de construire 974, dossier permis de construire, construire maison La Réunion, étude de sol obligatoire permis, bureau d'étude La Réunion"
    );
    setMetaProperty("og:title", "Permis de construire à La Réunion (974) | Solmis Habitat");
    setMetaProperty(
      "og:description",
      "Permis de construire à La Réunion : dossier complet, étude de sol incluse, accompagnement de A à Z avec un seul interlocuteur. Devis gratuit."
    );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-foreground min-h-screen">
      <Navbar />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden pt-36 md:pt-44 pb-20 md:pb-28">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--brand-terra)/0.08),transparent_70%)]" />
          </div>

          <div className="container-wide relative grid gap-16 md:grid-cols-12 items-center">
            <div className="md:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" />
                <span className="overline">Permis de construire · La Réunion (974)</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-medium leading-[1.07] text-primary">
                Construisez à{" "}
                <span className="italic font-light text-[hsl(var(--brand-terra))]">
                  La Réunion
                </span>{" "}
                sans mauvaise surprise.
              </h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl font-light text-primary/85 leading-snug">
                Permis de construire, étude de sol et accompagnement complet :
                un seul interlocuteur pour éviter{" "}
                <span className="text-[hsl(var(--brand-terra))] font-medium">
                  les refus, les retards et les surcoûts
                </span>
                .
              </p>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/#contact" data-testid="pc-cta-devis" className="btn-primary group">
                  Demander mon devis gratuit
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/#callback" data-testid="pc-cta-rappel" className="btn-outline">
                  <Phone size={16} strokeWidth={1.6} />
                  Être rappelé rapidement
                </Link>
              </div>

              <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={13} strokeWidth={2} className="text-[hsl(var(--brand-terra))]" />
                  Devis gratuit et sans engagement
                </span>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-[hsl(var(--brand-terra))]">⚡</span>
                  Réponse rapide sous 24h
                </span>
              </p>

              <div className="mt-6 inline-flex items-start gap-3 max-w-xl border-l-2 border-[hsl(var(--brand-terra))] bg-[hsl(var(--brand-terra)/0.06)] px-4 py-3.5">
                <span className="text-[hsl(var(--brand-terra))] text-lg leading-none mt-0.5">⚠</span>
                <p className="text-sm text-primary leading-snug">
                  <strong>Un permis de construire mal préparé peut entraîner un refus ou plusieurs mois de retard.</strong>
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-2xl">
                {[
                  "+100 projets réalisés",
                  "Expertise à La Réunion",
                  "Accompagnement de A à Z",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} strokeWidth={1.5} className="text-[hsl(var(--brand-terra))] mt-0.5 shrink-0" />
                    <span className="text-sm text-primary/85 font-medium">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-5 reveal">
              <div className="relative">
                <div className="absolute -top-4 -left-4 h-20 w-20 border-t border-l border-[hsl(var(--brand-terra))]" />
                <div className="absolute -bottom-4 -right-4 h-20 w-20 border-b border-r border-[hsl(var(--brand-terra))]" />
                <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                  <img
                    src={HERO_IMG}
                    alt="Permis de construire à La Réunion — accompagnement par Solmis Habitat"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLÈMES */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-wide">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              <div className="lg:col-span-5 reveal lg:sticky lg:top-32">
                <div className="flex items-center gap-3 mb-6">
                  <span className="rule" />
                  <span className="overline">Pour vous</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-medium leading-tight text-primary">
                  Vous êtes{" "}
                  <span className="italic font-light text-[hsl(var(--brand-terra))]">
                    dans ce cas
                  </span>{" "}
                  ?
                </h2>
                <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-md">
                  Le permis de construire à La Réunion soulève des questions
                  techniques et administratives que peu de particuliers
                  maîtrisent.
                </p>
                <Link
                  to="/#contact"
                  data-testid="pc-pb-cta"
                  className="mt-8 group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-7 py-4 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
                >
                  Parlons de votre projet
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              <ul className="lg:col-span-7 space-y-3">
                {cases.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <li
                      key={c.text}
                      className="reveal group bg-white border border-border p-6 md:p-7 flex items-start gap-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[hsl(var(--brand-terra))]"
                      style={{ transitionDelay: `${i * 60}ms` }}
                    >
                      <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl shrink-0">
                        <Icon size={22} strokeWidth={1.5} />
                      </span>
                      <p className="text-base md:text-lg leading-snug text-primary font-medium pt-2">
                        {c.text}
                      </p>
                      <ArrowRight size={18} strokeWidth={1.4} className="ml-auto mt-3 text-muted-foreground/30 transition-all duration-300 group-hover:text-[hsl(var(--brand-terra))] group-hover:translate-x-1 shrink-0" />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>

        {/* SOLUTION */}
        <section className="py-20 md:py-28">
          <div className="container-wide grid gap-16 lg:grid-cols-12 lg:gap-20 items-start">
            <div className="lg:col-span-5 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" />
                <span className="overline">Notre approche</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-medium leading-tight text-primary">
                Un accompagnement{" "}
                <span className="italic font-light">complet</span> pour votre
                permis de construire.
              </h2>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-lg leading-relaxed text-primary/85">
                Nous vous accompagnons à chaque étape de votre projet, de
                l'analyse à la préparation du dossier, avec{" "}
                <span className="text-[hsl(var(--brand-terra))] font-medium">
                  un seul interlocuteur
                </span>
                .
              </p>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Plus d'allers-retours entre architectes, géotechniciens et
                services d'urbanisme. Solmis Habitat orchestre l'ensemble du
                projet pour garantir un dossier conforme dès le premier dépôt.
              </p>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-wide">
            <div className="max-w-3xl mb-14 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">Nos services</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Un dossier{" "}
                <span className="italic font-light">prêt à déposer</span>.
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {services.map((s, i) => {
                const Icon = s.icon;
                return (
                  <article
                    key={s.title}
                    className="reveal bg-white border border-border p-7 hover:-translate-y-1 transition-all duration-300 hover:border-[hsl(var(--brand-terra))]"
                    style={{ transitionDelay: `${i * 70}ms` }}
                  >
                    <div className="flex items-start justify-between mb-6">
                      <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl">
                        <Icon size={22} strokeWidth={1.5} />
                      </span>
                      <span className="font-display text-2xl text-muted-foreground/30">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-xl text-primary leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {s.desc}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* LIEN ÉTUDE DE SOL */}
        <section className="py-20 md:py-28">
          <div className="container-narrow">
            <div className="grid md:grid-cols-12 gap-10 items-center reveal">
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-5">
                  <span className="rule" />
                  <span className="overline">Lien essentiel</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                  Pourquoi l'étude de sol est{" "}
                  <span className="italic font-light text-[hsl(var(--brand-terra))]">
                    essentielle
                  </span>{" "}
                  ?
                </h2>
                <p className="mt-6 text-base md:text-lg text-primary/85 leading-relaxed">
                  À La Réunion, les caractéristiques du terrain sont
                  déterminantes. Une étude de sol permet d'adapter votre
                  construction et d'éviter les problèmes de fondation.
                </p>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  Elle est également souvent indispensable pour constituer un
                  dossier de permis conforme et accélérer son instruction.
                </p>

                <Link
                  to="/etude-de-sol-la-reunion"
                  data-testid="pc-link-etude-sol"
                  className="mt-9 group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-7 py-4 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
                >
                  Découvrir notre service d'étude de sol
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="md:col-span-5">
                <Link
                  to="/etude-de-sol-la-reunion"
                  className="block group border border-border bg-white p-8 hover:border-[hsl(var(--brand-terra))] hover:-translate-y-1 transition-all duration-300"
                >
                  <p className="overline mb-3">Page liée</p>
                  <h3 className="font-display text-2xl text-primary leading-tight">
                    Étude de sol à La Réunion
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Missions G1 à G5, étude SPANC et accompagnement technique
                    pour sécuriser vos fondations.
                  </p>
                  <p className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1">
                    En savoir plus
                    <ArrowRight size={16} />
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ZONES D'INTERVENTION */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-wide">
            <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
              <div className="md:col-span-7 reveal">
                <div className="flex items-center gap-3 mb-5">
                  <span className="rule" />
                  <span className="overline">Zones d'intervention</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                  Permis de construire partout à{" "}
                  <span className="italic font-light">La Réunion</span>.
                </h2>
              </div>
              <div className="md:col-span-5 reveal">
                <p className="text-base text-muted-foreground leading-relaxed">
                  Nous intervenons sur l'ensemble du département (974) — côte
                  Ouest, Sud, Est, Nord et hauts inclus.
                </p>
              </div>
            </div>

            <ul className="reveal grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {cities.map((c) => (
                <li
                  key={c}
                  className="flex items-center gap-2.5 bg-white border border-border px-4 py-3 text-sm text-primary/85 hover:border-[hsl(var(--brand-terra))] transition-colors"
                >
                  <MapPin size={14} strokeWidth={1.5} className="text-[hsl(var(--brand-terra))] shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* POURQUOI NOUS */}
        <section className="py-20 md:py-28">
          <div className="container-wide">
            <div className="max-w-3xl mb-14 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">Pourquoi nous</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Cinq raisons de nous{" "}
                <span className="italic font-light">confier</span> votre permis.
              </h2>
            </div>

            <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-5 border border-border">
              {reasons.map((r, i) => (
                <div
                  key={r}
                  className="reveal bg-white p-7"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <CheckCircle2 size={20} strokeWidth={1.6} className="text-[hsl(var(--brand-terra))]" />
                  <p className="mt-5 font-display text-lg md:text-xl text-primary leading-snug">
                    {r}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PREUVES (placeholder visuel) */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-wide">
            <div className="max-w-3xl mb-12 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">Nos réalisations</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Une expertise terrain,{" "}
                <span className="italic font-light">démontrée</span>.
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-3 reveal">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-[4/3] bg-white border border-dashed border-border flex items-center justify-center"
                >
                  <div className="text-center px-6">
                    <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl mx-auto">
                      <Camera size={22} strokeWidth={1.5} />
                    </span>
                    <p className="mt-4 font-display text-lg text-primary">
                      Projet n°{i}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Photo bientôt disponible
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-8 text-center text-sm text-muted-foreground reveal">
              Photos de projets, interventions terrain et dossiers réalisés —
              à venir prochainement.
            </p>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-24 md:py-32 bg-primary text-primary-foreground">
          <div className="container-narrow text-center reveal">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
              C'est le moment
            </p>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl leading-[1.05]">
              Parlons de{" "}
              <span className="italic font-light text-white/85">
                votre projet
              </span>
              .
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl mx-auto">
              Chaque projet est unique, obtenez votre devis personnalisé dès
              maintenant.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/#contact"
                data-testid="pc-final-devis"
                className="group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
              >
                Demander mon devis
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pc-final-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1ebd5a]"
              >
                <MessageCircle size={16} strokeWidth={1.8} />
                WhatsApp direct
              </a>
            </div>
          </div>
        </section>

        {/* MAILLAGE INTERNE */}
        <section className="py-16 md:py-20">
          <div className="container-wide">
            <p className="overline mb-6 text-center">Aller plus loin</p>
            <div className="grid gap-6 md:grid-cols-3 reveal">
              <Link
                to="/etude-de-sol-la-reunion"
                className="group block bg-white border border-border p-7 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] transition-all duration-300"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                  Page liée
                </p>
                <h3 className="mt-2 font-display text-xl text-primary">
                  Étude de sol à La Réunion
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Analyse complète du terrain et missions G1 à G5.
                </p>
                <ArrowRight size={16} className="mt-5 text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/etude-geotechnique-la-reunion"
                className="group block bg-white border border-border p-7 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] transition-all duration-300"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                  Page liée
                </p>
                <h3 className="mt-2 font-display text-xl text-primary">
                  Étude géotechnique à La Réunion
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Missions normées, sondages, recommandations techniques.
                </p>
                <ArrowRight size={16} className="mt-5 text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/qui-sommes-nous"
                className="group block bg-white border border-border p-7 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] transition-all duration-300"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                  Page liée
                </p>
                <h3 className="mt-2 font-display text-xl text-primary">
                  Bureau d'études à La Réunion
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Notre méthode, notre équipe et notre réseau de partenaires.
                </p>
                <ArrowRight size={16} className="mt-5 text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProperty(prop, content) {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
